import { SectionContainer } from "@hatch-digital-nl/abs-storybook/legacy";
import {
    QualificationDetailPageInterface,
    setSection,
} from "../../QualificationDetailSlice";
import { useGetQualificationByIdQuery } from "../../../../common/api/qualification";
import { useParams } from "react-router";
import { useAppDispatch, useAppSelector } from "../../../../app/hooks";
import { useEstablishmentId } from "../../../../common/utils/useEstablishmentId";
import { JSX, useEffect } from "react";
import { useGetCertificatesQuery } from "../../../../common/api/certificate";
import { CertificateStatusEnum } from "../../../../common/enums/CertificateStatusEnum";
import { getSections } from "../../utils/getSections";
import { useGetEducatorsQuery } from "../../../../common/api/educator";
import { renderSectionContent } from "../../utils/renderSectionContent";
import { useGetEmployeesQuery } from "../../../../common/api/employee";
import { SectionEnum } from "../../../../common/enums/SectionEnum";
import { QualificationTypeEnum } from "../../../../common/enums/QualificationTypeEnum";
import Skeleton from "react-loading-skeleton";

export const EstablishmentManagerQualificationDetailContent =
    (): JSX.Element => {
        const dispatch = useAppDispatch();
        const qualificationId = Number(useParams().qualificationId);
        const {
            sortAchievedCertificates,
            section,
            sortEducators,
            sortEmployees,
        } = useAppSelector((state) => state.qualificationDetail);
        const qualificationDetail = useAppSelector(
            (state) => state.qualificationDetail,
        ) as QualificationDetailPageInterface;
        const { isPostCreateCertificateSuccess } = useAppSelector(
            (state) => state.createCertificateModal,
        );
        const { isPatchEditCertificateSuccess } = useAppSelector(
            (state) => state.editCertificateModal,
        );
        const { status } = qualificationDetail;

        const {
            data: certificates,
            isLoading: certificateIsLoading,
            refetch: certificateRefetch,
            isFetching: certificateIsFetching,
        } = useGetCertificatesQuery({
            items_per_page: 9999,
            filter: {
                qualification_id: qualificationId,
                establishment_id: useEstablishmentId(),
                status: status ? status : CertificateStatusEnum.ACTIVE,
            },
            ...(sortAchievedCertificates.sortName
                ? { sort: sortAchievedCertificates.sortName }
                : {}),
        });

        const { data: activeCertificates } = useGetCertificatesQuery({
            filter: {
                qualification_id: qualificationId,
                establishment_id: useEstablishmentId(),
                status: CertificateStatusEnum.ACTIVE,
            },
        });

        const { data: expiredCertificates } = useGetCertificatesQuery({
            filter: {
                qualification_id: qualificationId,
                establishment_id: useEstablishmentId(),
                status: CertificateStatusEnum.EXPIRED,
            },
        });

        const {
            data: educatorData,
            isLoading: educatorDataIsLoading,
            isFetching: educatorDataIsFetching,
        } = useGetEducatorsQuery({
            items_per_page: 9999,
            sort: sortEducators.sortName,
            filter: {
                qualification_id: qualificationId,
            },
        });

        const { data: qualificationData, isLoading: qualificationIsLoading } =
            useGetQualificationByIdQuery({ id: qualificationId });

        const {
            data: employeeData,
            isLoading: employeeDataIsLoading,
            isFetching: employeeDataIsFetching,
        } = useGetEmployeesQuery({
            items_per_page: 9999,
            sort: sortEmployees.sortName,
            filter: {
                establishment_id: useEstablishmentId(),
                missing_qualification_id: qualificationId,
            },
        });

        useEffect(() => {
            if (!qualificationData) {
                return;
            }

            if (qualificationData.type === QualificationTypeEnum.PERSON) {
                dispatch(
                    setSection({
                        name: SectionEnum.ACHIEVED_CERTIFICATES,
                        value: 0,
                    }),
                );

                return;
            }

            dispatch(
                setSection({
                    name: SectionEnum.ESTABLISHMENTS_CERTIFICATE,
                    value: 0,
                }),
            );
        }, [qualificationData, dispatch]);

        useEffect(() => {
            if (
                isPostCreateCertificateSuccess ||
                isPatchEditCertificateSuccess
            ) {
                certificateRefetch();
            }
        }, [
            isPostCreateCertificateSuccess,
            isPatchEditCertificateSuccess,
            certificateRefetch,
        ]);

        return (
            <div className="p-qualification-detail__content">
                {qualificationIsLoading ? (
                    <Skeleton height={"30rem"} borderRadius={"1.5rem"} />
                ) : (
                    <SectionContainer
                        sections={getSections({
                            dispatch,
                            amountEducators: educatorData?.meta?.total,
                            amountAchievedCertificates:
                                certificates?.meta?.total,
                            amountUnachievedEmployees:
                                employeeData?.meta?.total,
                            qualificationType: qualificationData?.type,
                        })}
                        activeIndex={section.value}
                    >
                        {renderSectionContent({
                            certificates,
                            qualificationData,
                            educatorData,
                            educatorDataIsFetching,
                            educatorDataIsLoading,
                            certificateIsLoading,
                            certificateIsFetching,
                            qualificationIsLoading,
                            employeeData,
                            employeeDataIsLoading,
                            employeeDataIsFetching,
                            sectionName: section.name,
                            totals: {
                                totalActive: activeCertificates?.meta?.total,
                                totalExpired: expiredCertificates?.meta?.total,
                            },
                        })}
                    </SectionContainer>
                )}
            </div>
        );
    };
